import styled from 'styled-components';
import ClickableContainer from '../RealtimeCCTV/ClickableContainer';
import ButtonContainer from '../RealtimeCCTV/ButtonContainer';
import { captureButton, removeQueueButton } from '../../../../components/button/DarkButtons';
import { WebSDKcctv, emptyCameraStatus } from '../../../../utils/webSDKcctvClass';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import LeftedToast from '../../../../components/cctv/LeftedToast';
import Portal from '../../../../components/Portal';
import { useRecoilValue } from 'recoil';
import { localCctvSettingState } from '../../../../atoms';
import CctvSettings from '../../../../components/Modal/CctvSettings';
import { timeToHHMMSS } from '../../../../utils/timeline';
import ClickExpandableContainer from '../RealtimeCCTV/ClickExpandableContainer';
import { formatDateToYYYYMMDDHHMMSS } from '../../../../utils/formatDate';

const Root = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  color: var(--white);
  user-select: none;
  gap: 0.25rem;
  > div:last-child {
    margin-left: 0.75rem;
  }
`;
interface Props {
  cctvOBJ: WebSDKcctv | undefined; // cctv 객체
  selectedCameraInfo: SelectedCctvCameraInfo;
  setSelectedCameraInfo: Dispatch<SetStateAction<SelectedCctvCameraInfo>>;
  nvrList: IMainMenu[];
  setNvrList: Dispatch<SetStateAction<IMainMenu[]>>;
  cctvSettings: CctvSettings;
  windows: {
    windowIndex: number;
    clock: {
      currentTime: number; //
      setCurrentTime: Dispatch<SetStateAction<number>>;
      setIsPlaying: Dispatch<SetStateAction<boolean>>;
      setSpeed: Dispatch<SetStateAction<1 | 2 | 4>>;
      speed: 1 | 2 | 4;
      currentDate: string;
      setCurrentDate: Dispatch<SetStateAction<string>>;
      isReversed: boolean;
      setIsReversed: Dispatch<SetStateAction<boolean>>;
    };
  }[];
}

const backToPage = () => {
  // navigate('/s_cctv1');
  // navigate(-1);
  window.history.back();
};

const ControllRightBar = ({ cctvOBJ, selectedCameraInfo, setSelectedCameraInfo, nvrList, setNvrList, cctvSettings, windows }: Props) => {
  const [openModal, setOpenModal] = useState({ type: 'cctvSetting', status: false });
  const disabled = selectedCameraInfo.windowIndex === null;
  const localCctvSettings = useRecoilValue(localCctvSettingState);

  useEffect(() => {
    if (cctvOBJ?.pluginOBJ !== undefined) {
      openModal.status ? cctvOBJ.hideWnd() : cctvOBJ.showWnd();
    }
  }, [openModal.status, cctvOBJ]);

  const handleOpenWindow = () => {
    if (localCctvSettings?.camMPath || cctvSettings.camMPath) {
      window.open(`/download_cctv`, '_blank', 'width=1200, height=600');
    } else {
      // 동영상 저장경로 설정 알럿
      toast(
        <LeftedToast btnText='설정' message='동영상 다운로드 폴더 경로를 설정하세요' onClick={() => setOpenModal({ type: 'cctvSetting', status: true })} />, //
        { position: 'top-left', autoClose: 5000 }
      );
    }
  };

  const onClickCaptureBtn = () => {
    if (localCctvSettings?.camCPath || cctvSettings.camCPath) {
      const findWindow = windows.find((window) => window.windowIndex === selectedCameraInfo.windowIndex);
      const findNvr = nvrList.find((nvr) => nvr.nCd === selectedCameraInfo.nCd);
      const fileName = `${findNvr?.nName}_${selectedCameraInfo.cName}_${findWindow?.clock.currentDate.replaceAll('-', '')}${timeToHHMMSS(findWindow?.clock.currentTime || 0).replaceAll(':', '')}`;
      cctvOBJ?.capture(fileName).then(() => {
        toast(
          <LeftedToast message='캡쳐 완료' onClick={() => cctvOBJ?.openDirectory((localCctvSettings?.camCPath || cctvSettings.camCPath) ?? undefined)} />, //
          { position: 'top-left', autoClose: 3000 }
        );
      });
    } else {
      // 캡쳐 저장경로 설정 알럿
      toast(
        <LeftedToast btnText='설정' message='캡쳐 다운로드 폴더 경로를 설정하세요' onClick={() => setOpenModal({ type: 'cctvSetting', status: true })} />, //
        { position: 'top-left', autoClose: 5000 }
      );
    }
  };
  const onClickStop = async () => {
    const videoWindow = windows.find((window) => window.windowIndex === selectedCameraInfo.windowIndex);
    if (cctvOBJ && videoWindow) {
      const { setIsPlaying, setCurrentTime, setCurrentDate } = videoWindow.clock;
      setIsPlaying(false);
      setCurrentTime(0);
      setCurrentDate('');

      const stopRes = await cctvOBJ?.stopWithIndex(videoWindow.windowIndex);
      if (stopRes !== undefined) {
        // 메뉴 목록 UI 업데이트
        const updateNvrList = nvrList.map((el: any) => {
          let newSubList = el.subList.map((camera: any) => {
            if (el.nCd === selectedCameraInfo.nCd && camera.cCd === selectedCameraInfo.cCd) {
              return {
                cCd: camera.cCd,
                cName: camera.cName,
                nCd: camera.nCd,
                stream: camera.stream,
                channelNum: camera.channelNum,
                ip: camera.ip,
                port: camera.port,
                pPort: camera.pPort,
                isPlaying: false,
                isRecording: false,
              };
            }
            return {
              ...camera,
            };
          });
          return { ...el, subList: newSubList };
        });
        setNvrList(updateNvrList);
        setSelectedCameraInfo(emptyCameraStatus);
      }
    }
  };

  const onClickStopAll = async () => {
    if (cctvOBJ) {
      windows.forEach((videoWindow) => {
        const { setIsPlaying, setCurrentTime, setCurrentDate } = videoWindow.clock;
        setIsPlaying(false);
        setCurrentTime(0);
        setCurrentDate('');
      });
      const stopRes = await cctvOBJ?.stop();
      if (stopRes !== undefined) {
        setSelectedCameraInfo(emptyCameraStatus);

        // 메뉴 목록 UI 업데이트
        const updateNvrList = nvrList.map((el: any) => {
          const newSubList = el.subList.map((camera: any) => ({
            cCd: camera.cCd,
            cName: camera.cName,
            nCd: camera.nCd,
            stream: camera.stream,
            channelNum: camera.channelNum,
            ip: camera.ip,
            port: camera.port,
            pPort: camera.pPort,
            isPlaying: false,
            isRecording: false,
          }));
          return { ...el, subList: newSubList };
        });
        setNvrList(updateNvrList);
      }
    }
  };
  const onClickRemoveScreen = async (value: 'selected' | 'all') => {
    if (value === 'selected') {
      onClickStop();
    } else {
      onClickStopAll();
    }
  };

  const onClickRecording = () => {
    if (selectedCameraInfo.isRecording) {
      cctvOBJ?.recordStopWithIndex(Number(selectedCameraInfo.windowIndex));
      toast(<LeftedToast message='자르기 완료' onClick={() => cctvOBJ?.openDirectory((localCctvSettings?.camMPath || cctvSettings.camMPath) ?? undefined)} />, {
        position: 'top-left',
        autoClose: 3000,
      });
    } else {
      cctvOBJ?.recordWithIndex(Number(selectedCameraInfo.windowIndex));
    }
  };

  const buttons = [
    {
      // 캡쳐
      key: 'capture',
      button: captureButton,
      onClick: () => onClickCaptureBtn(),
      disabled,
    },
    {
      // 줌인
      key: 'zoom_in',
      button: <ButtonContainer icon='frame_inspect' name='eZoom' darkModeOnly />,
      onClick: () => {
        if (selectedCameraInfo.windowIndex !== null) {
          cctvOBJ?.zoomEnable(selectedCameraInfo.windowIndex);
        }
      },
      disabled,
    },
    {
      // 자르기
      key: 'cut',
      button: <ButtonContainer icon='cut' name='자르기' darkModeOnly color={selectedCameraInfo.isPlaying && selectedCameraInfo.isRecording ? 'red' : ''} />,
      onClick: () => onClickRecording(),
      disabled: !selectedCameraInfo.isPlaying,
    },
    {
      // 다운로드
      key: 'download',
      button: <ButtonContainer icon='download' name='다운로드' darkModeOnly />,
      onClick: () => handleOpenWindow(),
    },
    {
      // 비우기
      key: 'tab_close',
      // button: <ButtonContainer icon='tab_close' name='비우기' darkModeOnly />,
      onClick: onClickRemoveScreen,
      disabled,
    },
    {
      // 종료
      key: 'close',
      button: <ButtonContainer icon='close' name='종료' darkModeOnly />,
      onClick: backToPage,
    },
  ];

  const removeScreenOptions = [
    { key: 'selected', button: <ButtonContainer name='선택한 채널만' darkModeOnly />, value: 'selected' },
    { key: 'all', button: <ButtonContainer name='채널 전체' darkModeOnly />, value: 'all' },
  ];
  return (
    <Root>
      {buttons.map((button) =>
        button.key === 'tab_close' ? (
          <ClickExpandableContainer // 화면 재생 빼기
            key={button.key}
            defaultOption={removeQueueButton}
            options={
              disabled
                ? removeScreenOptions //
                    .filter((option) => option.value !== 'selected')
                : removeScreenOptions
            }
            direction='toLeft'
            onClick={(removeOption) => onClickRemoveScreen(removeOption)}
            disabled={button.disabled}
          />
        ) : (
          <ClickableContainer //
            key={button.key}
            button={button.button}
            onClick={button.onClick}
            disabled={button.disabled}
          />
        )
      )}
      {cctvOBJ && (
        <Portal openModal={openModal?.status}>
          <CctvSettings
            setOpenModal={setOpenModal} //
            cctvSettings={cctvSettings}
            cctvOBJ={cctvOBJ}
            isPlaycctv
          />
        </Portal>
      )}
    </Root>
  );
};

export default ControllRightBar;
